import { applyDependencies, dependencyDisabled } from 'components/users/permissions/permission_dependencies';

import type { DependencyRule } from 'components/users/permissions/permission_dependencies';
import type { CatalogGroup, CatalogRow } from './types';

export type RoleValues = Record<string, boolean>;

// The graph helpers take an office_admin flag because the user's permissions page renders that toggle
// outside its checkbox grid. It cannot appear here at all: office_admin is not role-assignable, so
// RolePermissionCatalog never offers it and no role can ever grant it.
const OFFICE_ADMIN_GRANTED = false;

export const rowsOf = (groups: CatalogGroup[]): CatalogRow[] => (
  groups.reduce<CatalogRow[]>((all, group) => all.concat(group.rows), [])
);

// Every permission on the page, with the ones this role grants switched on. Keyed over the CATALOG rather
// than over the role's grants, so a role holding a grant the company can no longer see does not smuggle it
// into the next save — and so "revoke everything" submits explicit falses rather than an empty map.
export const valuesFor = (rows: CatalogRow[], granted: string[]): RoleValues => {
  const values: RoleValues = {};

  rows.forEach((row) => {
    values[row.key] = granted.includes(row.key);
  });

  return values;
};

// Moving one toggle, with the dependency graph re-applied. `values` is passed as the previous state so a
// revoked permission also releases anything it had forced on — a transition, not a steady state.
export const withValue = (
  rules: DependencyRule[], values: RoleValues, key: string, value: boolean,
): RoleValues => applyDependencies(rules, { ...values, [key]: value }, OFFICE_ADMIN_GRANTED, values);

export const isLocked = (
  rules: DependencyRule[], values: RoleValues, key: string,
): boolean => dependencyDisabled(rules, key, values, OFFICE_ADMIN_GRANTED);

// The prerequisites a permission is still missing, by label. The server rejects such a combination
// outright, so the point of saying it here is that the admin never has to submit to find out.
export const blockedBy = (rows: CatalogRow[], values: RoleValues, key: string): string[] => {
  if (values[key]) return [];

  const row = rows.find(candidate => candidate.key === key);
  const labels = new Map(rows.map(candidate => [candidate.key, candidate.label]));

  return (row?.requires ?? []).filter(requirement => !values[requirement])
    .map(requirement => labels.get(requirement) ?? requirement);
};

export const grantedCount = (values: RoleValues): number => (
  Object.values(values).filter(Boolean).length
);

export const changedFrom = (values: RoleValues, granted: string[]): boolean => (
  Object.keys(values).some(key => values[key] !== granted.includes(key))
);
